import { useState, useCallback, useEffect, useRef } from 'react';
import { saveToLocalStorage, loadFromLocalStorage, addToRecentProjects } from '../utils/exportUtils';
import { getTemplate } from '../templates/projectTemplates';

const MAX_HISTORY = 50;

export const useProject = () => {
  const [projectName, setProjectName] = useState('Untitled Project');
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [swimlanes, setSwimlanes] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedSwimlaneId, setSelectedSwimlaneId] = useState(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [canvas, setCanvas] = useState({ zoom: 1, panX: 0, panY: 0, showGrid: true });
  const [isDirty, setIsDirty] = useState(false);

  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState({ nodes: [], connections: [] });

  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);

  // Next IDs
  const nextNodeId = useRef(1);
  const nextConnectionId = useRef(1);
  const nextGroupId = useRef(1);
  const nextSwimlaneId = useRef(1);

  // Save state to history
  const saveToHistory = useCallback((newNodes, newConnections, newGroups = groups, newSwimlanes = swimlanes) => {
    if (isUndoRedo.current) {
      isUndoRedo.current = false;
      return;
    }

    const newState = {
      nodes: JSON.parse(JSON.stringify(newNodes)),
      connections: JSON.parse(JSON.stringify(newConnections)),
      groups: JSON.parse(JSON.stringify(newGroups)),
      swimlanes: JSON.parse(JSON.stringify(newSwimlanes))
    };

    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newState);
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [historyIndex, groups, swimlanes]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedo.current = true;
      const prevState = history[historyIndex - 1];
      setNodes(prevState.nodes);
      setConnections(prevState.connections);
      setGroups(prevState.groups || []);
      setSwimlanes(prevState.swimlanes || []);
      setHistoryIndex(prev => prev - 1);
      setIsDirty(true);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedo.current = true;
      const nextState = history[historyIndex + 1];
      setNodes(nextState.nodes);
      setConnections(nextState.connections);
      setGroups(nextState.groups || []);
      setSwimlanes(nextState.swimlanes || []);
      setHistoryIndex(prev => prev + 1);
      setIsDirty(true);
    }
  }, [history, historyIndex]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Load from localStorage on mount
  useEffect(() => {
    const saved = loadFromLocalStorage();
    if (saved) {
      setProjectName(saved.name || 'Untitled Project');
      setNodes(saved.nodes || []);
      setConnections(saved.connections || []);
      setGroups(saved.groups || []);
      setSwimlanes(saved.swimlanes || []);
      setCanvas(saved.canvas || { zoom: 1, panX: 0, panY: 0, showGrid: true });

      if (saved.nodes?.length > 0) {
        nextNodeId.current = Math.max(...saved.nodes.map(n => n.id)) + 1;
      }
      if (saved.connections?.length > 0) {
        nextConnectionId.current = Math.max(...saved.connections.map(c => c.id)) + 1;
      }
      if (saved.groups?.length > 0) {
        nextGroupId.current = Math.max(...saved.groups.map(g => g.id)) + 1;
      }
      if (saved.swimlanes?.length > 0) {
        nextSwimlaneId.current = Math.max(...saved.swimlanes.map(s => s.id)) + 1;
      }

      // Initialize history with loaded state
      setHistory([{
        nodes: JSON.parse(JSON.stringify(saved.nodes || [])),
        connections: JSON.parse(JSON.stringify(saved.connections || [])),
        groups: JSON.parse(JSON.stringify(saved.groups || [])),
        swimlanes: JSON.parse(JSON.stringify(saved.swimlanes || []))
      }]);
      setHistoryIndex(0);
    }
  }, []);

  // Auto-save effect
  useEffect(() => {
    if (isDirty) {
      const timer = setTimeout(() => {
        saveToLocalStorage({
          name: projectName,
          nodes,
          connections,
          groups,
          swimlanes,
          canvas
        });
        setIsDirty(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [projectName, nodes, connections, groups, swimlanes, canvas, isDirty]);

  // Select node (with multi-select support)
  const selectNode = useCallback((id, addToSelection = false) => {
    if (addToSelection) {
      setSelectedNodeIds(prev => {
        if (prev.includes(id)) {
          return prev.filter(nodeId => nodeId !== id);
        }
        return [...prev, id];
      });
    } else {
      setSelectedNodeIds([id]);
    }
    setSelectedConnectionId(null);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedNodeIds([]);
    setSelectedConnectionId(null);
  }, []);

  // Add a new node
  const addNode = useCallback((type, x = 200, y = 200) => {
    const nodeDefaults = {
      rectangle: { width: 140, height: 60, color: '#3b82f6', text: 'Process' },
      rounded: { width: 120, height: 50, color: '#10b981', text: 'Terminal' },
      diamond: { width: 140, height: 100, color: '#f59e0b', text: 'Decision?' },
      parallelogram: { width: 150, height: 60, color: '#8b5cf6', text: 'Input/Output' },
      text: { width: 100, height: 40, color: 'transparent', text: 'Text' }
    };

    const defaults = nodeDefaults[type] || nodeDefaults.rectangle;

    const newNode = {
      id: nextNodeId.current++,
      x,
      y,
      text: defaults.text,
      type,
      color: defaults.color,
      width: defaults.width,
      height: defaults.height
    };

    setNodes(prev => {
      const updated = [...prev, newNode];
      saveToHistory(updated, connections);
      return updated;
    });
    setIsDirty(true);
    setSelectedNodeIds([newNode.id]);

    return newNode;
  }, [connections, saveToHistory]);

  // Update a node
  const updateNode = useCallback((id, updates) => {
    setNodes(prev => {
      const updated = prev.map(node =>
        node.id === id ? { ...node, ...updates } : node
      );
      saveToHistory(updated, connections);
      return updated;
    });
    setIsDirty(true);
  }, [connections, saveToHistory]);

  // Update multiple nodes (for dragging selection)
  const updateNodes = useCallback((updates) => {
    setNodes(prev => {
      const updated = prev.map(node => {
        const nodeUpdate = updates.find(u => u.id === node.id);
        return nodeUpdate ? { ...node, ...nodeUpdate.changes } : node;
      });
      saveToHistory(updated, connections);
      return updated;
    });
    setIsDirty(true);
  }, [connections, saveToHistory]);

  // Delete a node and its connections
  const deleteNode = useCallback((id) => {
    setNodes(prev => {
      const updated = prev.filter(node => node.id !== id);
      const updatedConnections = connections.filter(
        conn => conn.from !== id && conn.to !== id
      );
      setConnections(updatedConnections);
      saveToHistory(updated, updatedConnections);
      return updated;
    });
    setSelectedNodeIds(prev => prev.filter(nodeId => nodeId !== id));
    setIsDirty(true);
  }, [connections, saveToHistory]);

  // Delete multiple nodes
  const deleteSelectedNodes = useCallback(() => {
    if (selectedNodeIds.length === 0) return;

    setNodes(prev => {
      const updated = prev.filter(node => !selectedNodeIds.includes(node.id));
      const updatedConnections = connections.filter(
        conn => !selectedNodeIds.includes(conn.from) && !selectedNodeIds.includes(conn.to)
      );
      setConnections(updatedConnections);
      saveToHistory(updated, updatedConnections);
      return updated;
    });
    setSelectedNodeIds([]);
    setIsDirty(true);
  }, [selectedNodeIds, connections, saveToHistory]);

  // Copy selected nodes to clipboard
  const copySelectedNodes = useCallback(() => {
    if (selectedNodeIds.length === 0) return;

    const nodesToCopy = nodes.filter(n => selectedNodeIds.includes(n.id));
    const connectionsToCopy = connections.filter(
      c => selectedNodeIds.includes(c.from) && selectedNodeIds.includes(c.to)
    );

    setClipboard({
      nodes: JSON.parse(JSON.stringify(nodesToCopy)),
      connections: JSON.parse(JSON.stringify(connectionsToCopy))
    });
  }, [selectedNodeIds, nodes, connections]);

  // Paste from clipboard
  const pasteNodes = useCallback(() => {
    if (clipboard.nodes.length === 0) return;

    const idMapping = {};
    const newNodes = [];
    const newConnections = [];

    // Create new nodes with offset
    clipboard.nodes.forEach(node => {
      const newId = nextNodeId.current++;
      idMapping[node.id] = newId;
      newNodes.push({
        ...node,
        id: newId,
        x: node.x + 40,
        y: node.y + 40
      });
    });

    // Recreate connections with new IDs
    clipboard.connections.forEach(conn => {
      newConnections.push({
        ...conn,
        id: nextConnectionId.current++,
        from: idMapping[conn.from],
        to: idMapping[conn.to]
      });
    });

    setNodes(prev => {
      const updated = [...prev, ...newNodes];
      saveToHistory(updated, [...connections, ...newConnections]);
      return updated;
    });

    setConnections(prev => [...prev, ...newConnections]);
    setSelectedNodeIds(newNodes.map(n => n.id));
    setIsDirty(true);

    // Update clipboard with new positions for subsequent pastes
    setClipboard({
      nodes: newNodes.map(n => ({ ...n, x: n.x, y: n.y })),
      connections: newConnections
    });
  }, [clipboard, connections, saveToHistory]);

  // Legacy single copy (duplicate immediately)
  const copyNode = useCallback((id) => {
    const node = nodes.find(n => n.id === id);
    if (node) {
      const newNode = {
        ...node,
        id: nextNodeId.current++,
        x: node.x + 30,
        y: node.y + 30
      };
      setNodes(prev => {
        const updated = [...prev, newNode];
        saveToHistory(updated, connections);
        return updated;
      });
      setSelectedNodeIds([newNode.id]);
      setIsDirty(true);
      return newNode;
    }
    return null;
  }, [nodes, connections, saveToHistory]);

  // Add a connection
  const addConnection = useCallback((fromId, toId, label = '') => {
    // Check if connection already exists
    const exists = connections.some(
      conn => conn.from === fromId && conn.to === toId
    );

    if (exists || fromId === toId) {
      return null;
    }

    const newConnection = {
      id: nextConnectionId.current++,
      from: fromId,
      to: toId,
      label,
      style: 'solid'
    };

    setConnections(prev => {
      const updated = [...prev, newConnection];
      saveToHistory(nodes, updated);
      return updated;
    });
    setIsDirty(true);

    return newConnection;
  }, [connections, nodes, saveToHistory]);

  // Update a connection
  const updateConnection = useCallback((id, updates) => {
    setConnections(prev => {
      const updated = prev.map(conn =>
        conn.id === id ? { ...conn, ...updates } : conn
      );
      saveToHistory(nodes, updated);
      return updated;
    });
    setIsDirty(true);
  }, [nodes, saveToHistory]);

  // Delete a connection
  const deleteConnection = useCallback((id) => {
    setConnections(prev => {
      const updated = prev.filter(conn => conn.id !== id);
      saveToHistory(nodes, updated);
      return updated;
    });
    setSelectedConnectionId(null);
    setIsDirty(true);
  }, [nodes, saveToHistory]);

  // Toggle grid
  const toggleGrid = useCallback(() => {
    setCanvas(prev => ({ ...prev, showGrid: !prev.showGrid }));
    setIsDirty(true);
  }, []);

  // Create a group from selected nodes
  const createGroup = useCallback((nodeIds, label = 'Group') => {
    if (nodeIds.length === 0) return null;

    const selectedNodes = nodes.filter(n => nodeIds.includes(n.id));
    if (selectedNodes.length === 0) return null;

    // Calculate bounding box with padding
    const padding = 20;
    const headerHeight = 40;
    const minX = Math.min(...selectedNodes.map(n => n.x)) - padding;
    const maxX = Math.max(...selectedNodes.map(n => n.x + n.width)) + padding;
    const minY = Math.min(...selectedNodes.map(n => n.y)) - padding - headerHeight;
    const maxY = Math.max(...selectedNodes.map(n => n.y + n.height)) + padding;

    const newGroup = {
      id: nextGroupId.current++,
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      label,
      color: '#94a3b8',
      nodeIds: [...nodeIds]
    };

    setGroups(prev => {
      const updated = [...prev, newGroup];
      saveToHistory(nodes, connections, updated);
      return updated;
    });
    setIsDirty(true);
    setSelectedGroupId(newGroup.id);
    setSelectedNodeIds([]);

    return newGroup;
  }, [nodes, connections, saveToHistory]);

  // Update a group
  const updateGroup = useCallback((id, updates) => {
    setGroups(prev => {
      const updated = prev.map(group =>
        group.id === id ? { ...group, ...updates } : group
      );
      saveToHistory(nodes, connections, updated);
      return updated;
    });
    setIsDirty(true);
  }, [nodes, connections, saveToHistory]);

  // Delete a group (keeps nodes)
  const deleteGroup = useCallback((id) => {
    setGroups(prev => {
      const updated = prev.filter(group => group.id !== id);
      saveToHistory(nodes, connections, updated);
      return updated;
    });
    setSelectedGroupId(null);
    setIsDirty(true);
  }, [nodes, connections, saveToHistory]);

  // Add nodes to group
  const addNodesToGroup = useCallback((groupId, nodeIds) => {
    setGroups(prev => {
      const updated = prev.map(group => {
        if (group.id === groupId) {
          const newNodeIds = [...new Set([...group.nodeIds, ...nodeIds])];
          return { ...group, nodeIds: newNodeIds };
        }
        return group;
      });
      saveToHistory(nodes, connections, updated);
      return updated;
    });
    setIsDirty(true);
  }, [nodes, connections, saveToHistory]);

  // Remove nodes from group
  const removeNodesFromGroup = useCallback((groupId, nodeIds) => {
    setGroups(prev => {
      const updated = prev.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            nodeIds: group.nodeIds.filter(id => !nodeIds.includes(id))
          };
        }
        return group;
      });
      saveToHistory(nodes, connections, updated);
      return updated;
    });
    setIsDirty(true);
  }, [nodes, connections, saveToHistory]);

  // Select group
  const selectGroup = useCallback((id) => {
    setSelectedGroupId(id);
    setSelectedNodeIds([]);
    setSelectedSwimlaneId(null);
    setSelectedConnectionId(null);
  }, []);

  // Add a swimlane
  const addSwimlane = useCallback((label = 'New Lane') => {
    // Calculate x position based on existing swimlanes
    const existingLanes = swimlanes.length;
    const defaultWidth = 250;
    const x = swimlanes.reduce((sum, lane) => sum + (lane.width || defaultWidth), 0);

    const colors = ['#dbeafe', '#dcfce7', '#fef3c7', '#fce7f3', '#e0e7ff', '#f3e8ff'];
    const color = colors[existingLanes % colors.length];

    const newSwimlane = {
      id: nextSwimlaneId.current++,
      label,
      x,
      width: defaultWidth,
      color
    };

    setSwimlanes(prev => {
      const updated = [...prev, newSwimlane];
      saveToHistory(nodes, connections, groups, updated);
      return updated;
    });
    setIsDirty(true);
    setSelectedSwimlaneId(newSwimlane.id);

    return newSwimlane;
  }, [swimlanes, nodes, connections, groups, saveToHistory]);

  // Update a swimlane
  const updateSwimlane = useCallback((id, updates) => {
    setSwimlanes(prev => {
      const updated = prev.map(lane =>
        lane.id === id ? { ...lane, ...updates } : lane
      );

      // Recalculate x positions if width changed
      if (updates.width !== undefined) {
        let currentX = 0;
        updated.forEach(lane => {
          lane.x = currentX;
          currentX += lane.width || 250;
        });
      }

      saveToHistory(nodes, connections, groups, updated);
      return updated;
    });
    setIsDirty(true);
  }, [nodes, connections, groups, saveToHistory]);

  // Delete a swimlane
  const deleteSwimlane = useCallback((id) => {
    setSwimlanes(prev => {
      const updated = prev.filter(lane => lane.id !== id);

      // Recalculate x positions
      let currentX = 0;
      updated.forEach(lane => {
        lane.x = currentX;
        currentX += lane.width || 250;
      });

      saveToHistory(nodes, connections, groups, updated);
      return updated;
    });
    setSelectedSwimlaneId(null);
    setIsDirty(true);
  }, [nodes, connections, groups, saveToHistory]);

  // Reorder swimlanes
  const reorderSwimlanes = useCallback((fromIndex, toIndex) => {
    setSwimlanes(prev => {
      const updated = [...prev];
      const [removed] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, removed);

      // Recalculate x positions
      let currentX = 0;
      updated.forEach(lane => {
        lane.x = currentX;
        currentX += lane.width || 250;
      });

      saveToHistory(nodes, connections, groups, updated);
      return updated;
    });
    setIsDirty(true);
  }, [nodes, connections, groups, saveToHistory]);

  // Select swimlane
  const selectSwimlane = useCallback((id) => {
    setSelectedSwimlaneId(id);
    setSelectedNodeIds([]);
    setSelectedGroupId(null);
    setSelectedConnectionId(null);
  }, []);

  // Load a template
  const loadTemplate = useCallback((templateKey) => {
    const template = getTemplate(templateKey);
    setNodes(template.nodes);
    setConnections(template.connections);
    setGroups([]);
    setSwimlanes([]);

    if (template.nodes.length > 0) {
      nextNodeId.current = Math.max(...template.nodes.map(n => n.id)) + 1;
    } else {
      nextNodeId.current = 1;
    }

    if (template.connections.length > 0) {
      nextConnectionId.current = Math.max(...template.connections.map(c => c.id)) + 1;
    } else {
      nextConnectionId.current = 1;
    }
    nextGroupId.current = 1;
    nextSwimlaneId.current = 1;

    // Reset history
    setHistory([{
      nodes: JSON.parse(JSON.stringify(template.nodes)),
      connections: JSON.parse(JSON.stringify(template.connections)),
      groups: [],
      swimlanes: []
    }]);
    setHistoryIndex(0);

    setSelectedNodeIds([]);
    setSelectedGroupId(null);
    setSelectedSwimlaneId(null);
    setSelectedConnectionId(null);
    setIsDirty(true);
  }, []);

  // Load project data
  const loadProject = useCallback((projectData) => {
    setProjectName(projectData.name || 'Imported Project');
    setNodes(projectData.nodes || []);
    setConnections(projectData.connections || []);
    setGroups(projectData.groups || []);
    setSwimlanes(projectData.swimlanes || []);
    setCanvas(projectData.canvas || { zoom: 1, panX: 0, panY: 0, showGrid: true });

    if (projectData.nodes?.length > 0) {
      nextNodeId.current = Math.max(...projectData.nodes.map(n => n.id)) + 1;
    } else {
      nextNodeId.current = 1;
    }

    if (projectData.connections?.length > 0) {
      nextConnectionId.current = Math.max(...projectData.connections.map(c => c.id)) + 1;
    } else {
      nextConnectionId.current = 1;
    }

    if (projectData.groups?.length > 0) {
      nextGroupId.current = Math.max(...projectData.groups.map(g => g.id)) + 1;
    } else {
      nextGroupId.current = 1;
    }

    if (projectData.swimlanes?.length > 0) {
      nextSwimlaneId.current = Math.max(...projectData.swimlanes.map(s => s.id)) + 1;
    } else {
      nextSwimlaneId.current = 1;
    }

    // Reset history
    setHistory([{
      nodes: JSON.parse(JSON.stringify(projectData.nodes || [])),
      connections: JSON.parse(JSON.stringify(projectData.connections || [])),
      groups: JSON.parse(JSON.stringify(projectData.groups || [])),
      swimlanes: JSON.parse(JSON.stringify(projectData.swimlanes || []))
    }]);
    setHistoryIndex(0);

    setSelectedNodeIds([]);
    setSelectedGroupId(null);
    setSelectedSwimlaneId(null);
    setSelectedConnectionId(null);
    setIsDirty(true);

    addToRecentProjects(projectData);
  }, []);

  // Reset project
  const resetProject = useCallback(() => {
    setProjectName('Untitled Project');
    setNodes([]);
    setConnections([]);
    setGroups([]);
    setSwimlanes([]);
    setCanvas({ zoom: 1, panX: 0, panY: 0, showGrid: true });
    setSelectedNodeIds([]);
    setSelectedGroupId(null);
    setSelectedSwimlaneId(null);
    setSelectedConnectionId(null);
    setConnectingFrom(null);
    nextNodeId.current = 1;
    nextConnectionId.current = 1;
    nextGroupId.current = 1;
    nextSwimlaneId.current = 1;

    // Reset history
    setHistory([{ nodes: [], connections: [], groups: [], swimlanes: [] }]);
    setHistoryIndex(0);

    setIsDirty(true);
  }, []);

  // Start connecting
  const startConnecting = useCallback((nodeId) => {
    setConnectingFrom(nodeId);
  }, []);

  // Complete connecting
  const completeConnecting = useCallback((toNodeId) => {
    let newConnection = null;
    if (connectingFrom && connectingFrom !== toNodeId) {
      newConnection = addConnection(connectingFrom, toNodeId);
    }
    setConnectingFrom(null);
    return newConnection;
  }, [connectingFrom, addConnection]);

  // Cancel connecting
  const cancelConnecting = useCallback(() => {
    setConnectingFrom(null);
  }, []);

  // For backwards compatibility
  const selectedNodeId = selectedNodeIds.length === 1 ? selectedNodeIds[0] : null;
  const setSelectedNodeId = useCallback((id) => {
    if (id === null) {
      setSelectedNodeIds([]);
    } else {
      setSelectedNodeIds([id]);
    }
  }, []);

  return {
    projectName,
    setProjectName,
    nodes,
    connections,
    groups,
    swimlanes,
    selectedNodeId,
    setSelectedNodeId,
    selectedNodeIds,
    setSelectedNodeIds,
    selectNode,
    clearSelection,
    selectedGroupId,
    setSelectedGroupId,
    selectGroup,
    selectedSwimlaneId,
    setSelectedSwimlaneId,
    selectSwimlane,
    selectedConnectionId,
    setSelectedConnectionId,
    connectingFrom,
    canvas,
    setCanvas,
    isDirty,
    clipboard,

    // Node operations
    addNode,
    updateNode,
    updateNodes,
    deleteNode,
    deleteSelectedNodes,
    copyNode,
    copySelectedNodes,
    pasteNodes,

    // Connection operations
    addConnection,
    updateConnection,
    deleteConnection,

    // Group operations
    createGroup,
    updateGroup,
    deleteGroup,
    addNodesToGroup,
    removeNodesFromGroup,

    // Swimlane operations
    addSwimlane,
    updateSwimlane,
    deleteSwimlane,
    reorderSwimlanes,

    // Canvas operations
    toggleGrid,

    // Connecting flow
    startConnecting,
    completeConnecting,
    cancelConnecting,

    // Project operations
    loadTemplate,
    loadProject,
    resetProject,

    // History
    undo,
    redo,
    canUndo,
    canRedo
  };
};

export default useProject;
